import { useEffect, useState, useRef, useCallback } from 'react';

interface BlockedRequest {
  method: string;
  endpoint: string;
  payload?: unknown;
  timestamp: Date;
}

/**
 * Component that displays blocked write requests in a resizable collapsible drawer.
 * Shows a horizontal tab at the bottom-left that opens upward to reveal all blocked requests.
 */
const ProductionReadonlyDrawer: React.FC = () => {
  const [blockedRequests, setBlockedRequests] = useState<BlockedRequest[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState({ width: 500, height: 400 });
  const [isResizing, setIsResizing] = useState<'right' | 'top' | 'corner' | null>(null);
  const drawerRef = useRef<HTMLDivElement>(null);
  const startPos = useRef({ x: 0, y: 0, width: 0, height: 0 });

  useEffect(() => {
    const handleBlockedWrite = (event: CustomEvent<Omit<BlockedRequest, 'timestamp'>>) => {
      const { method, endpoint, payload } = event.detail;

      setBlockedRequests((prev) => [
        ...prev,
        { method, endpoint, payload, timestamp: new Date() },
      ]);

      // Auto-open drawer when new message arrives
      setIsOpen(true);
    };

    window.addEventListener('production-readonly-blocked', handleBlockedWrite as EventListener);

    return () => {
      window.removeEventListener('production-readonly-blocked', handleBlockedWrite as EventListener);
    };
  }, []);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isResizing) return;

    const deltaX = e.clientX - startPos.current.x;
    const deltaY = startPos.current.y - e.clientY;

    if (isResizing === 'right' || isResizing === 'corner') {
      const newWidth = Math.max(300, Math.min(1200, startPos.current.width + deltaX));
      setSize(prev => ({ ...prev, width: newWidth }));
    }
    if (isResizing === 'top' || isResizing === 'corner') {
      const maxHeight = window.innerHeight - 100; // Allow almost full page height
      const newHeight = Math.max(200, Math.min(maxHeight, startPos.current.height + deltaY));
      setSize(prev => ({ ...prev, height: newHeight }));
    }
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(null);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = isResizing === 'corner' ? 'nesw-resize' :
                                   isResizing === 'right' ? 'ew-resize' : 'ns-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const startResize = (e: React.MouseEvent, direction: 'right' | 'top' | 'corner') => {
    e.preventDefault();
    setIsResizing(direction);
    startPos.current = {
      x: e.clientX,
      y: e.clientY,
      width: size.width,
      height: size.height,
    };
  };

  const clearMessages = () => {
    setBlockedRequests([]);
  };

  const formatPayload = (payload: unknown): string => {
    if (!payload) return '';
    try {
      return JSON.stringify(payload, null, 2);
    } catch {
      return '[Unable to serialize payload]';
    }
  };

  const copyToClipboard = (req: BlockedRequest) => {
    const text = `${req.method} ${req.endpoint}\n${req.payload ? JSON.stringify(req.payload, null, 2) : '(no payload)'}`;
    navigator.clipboard.writeText(text);
  };

  // Don't render if no messages
  if (blockedRequests.length === 0) {
    return null;
  }

  return (
    <div
      style={{ zIndex: 99999 }}
      className="fixed bottom-0 left-4 flex flex-col items-start"
    >
      {/* Tab button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 bg-orange-500 text-white rounded-t-lg shadow-lg hover:bg-orange-600 transition-colors"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
            clipRule="evenodd"
          />
        </svg>
        <span className="font-medium">Blocked Writes</span>
        <span className="bg-white text-orange-600 rounded-full px-2 py-0.5 text-sm font-bold">
          {blockedRequests.length}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {/* Drawer content */}
      {isOpen && (
        <div
          ref={drawerRef}
          style={{ width: size.width, height: size.height }}
          className="bg-gray-900 text-white rounded-tr-lg rounded-b-lg shadow-2xl flex flex-col relative"
        >
          {/* Resize handle - right edge */}
          <div
            onMouseDown={(e) => startResize(e, 'right')}
            className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-orange-500/30 transition-colors"
          />

          {/* Resize handle - top edge */}
          <div
            onMouseDown={(e) => startResize(e, 'top')}
            className="absolute top-0 left-0 right-0 h-2 cursor-ns-resize hover:bg-orange-500/30 transition-colors"
          />

          {/* Resize handle - corner */}
          <div
            onMouseDown={(e) => startResize(e, 'corner')}
            className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize hover:bg-orange-500/50 transition-colors rounded-tr-lg"
          />

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-700">
            <span className="text-sm text-gray-400">
              Production Readonly Mode - All writes blocked
            </span>
            <button
              onClick={clearMessages}
              className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded hover:bg-gray-700"
            >
              Clear All
            </button>
          </div>

          {/* Messages */}
          <div className="overflow-y-auto flex-1 p-2 space-y-2">
            {blockedRequests.map((req, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded p-3 text-sm border-l-4 border-orange-500"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-orange-400">
                    {req.method} {req.endpoint}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => copyToClipboard(req)}
                      className="text-xs text-gray-400 hover:text-white px-1.5 py-0.5 rounded hover:bg-gray-700"
                      title="Copy to clipboard"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                        <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                      </svg>
                    </button>
                    <span className="text-xs text-gray-500">
                      {req.timestamp.toLocaleTimeString()}
                    </span>
                  </div>
                </div>
                {req.payload !== undefined && (
                  <pre className="text-xs text-gray-300 bg-gray-900 p-2 rounded mt-2 overflow-x-auto max-h-32 overflow-y-auto">
                    {formatPayload(req.payload)}
                  </pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductionReadonlyDrawer;
